import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import {
  ThermometerApiEnvelope,
  ThermometerCertificateRequest,
  ThermometerCertificateResponse,
  ThermometerResult
} from 'src/app/Interface/DataEntity/DataEntityThermometerForm_Interface';
import { AlertifyService } from 'src/app/Service/Alertify.service';
import { DataEntityThermometerFormService } from 'src/app/Service/DataEntity/DataEntityThermometerForm.service';
import {
  acceptanceLimitsValidator,
  finiteNumberValidator,
  isFiniteNumericValue,
  requiredTextValidator,
  requiredSelectionValidator,
  nonNegativeFiniteNumberValidator,
  orderedRangeValidator,
  positiveFiniteNumberValidator,
  thermometerArraysValidator
} from './data-entity-thermometer-form.validators';

import { thermometerFieldHelp, thermometerHelpText, ThermometerFieldHelp } from './data-entity-thermometer-form.help';

@Component({
  selector: 'app-data-entity-thermometer-form',
  templateUrl: './DataEntityThermometerForm.component.html',
  styleUrls: ['./DataEntityThermometerForm.component.scss']
})
export class DataEntityThermometerFormComponent implements OnInit, OnDestroy {
  form: FormGroup;
  legacyMeasurements: any[] = [];
  isLoadingInitialData = false;
  isSubmitting = false;
  submitAttempted = false;
  isDownloading = false;
  certificateRegistrationStatus: 'unknown' | 'registered' | 'notRegistered' = 'unknown';
  response: ThermometerCertificateResponse = null;
  certificateFilePath: string = null;
  uncertaintyReportFilePath: string = null;
  expandedResultRows: boolean[] = [];

  readonly generalFields = [
    'Description', 'Expert', 'IssueDateMiladi', 'CalibrationDateMiladi',
    'RecalibrationDateMiladi', 'CustomerName', 'CertificateAddress', 'LabAdderss'
  ];
  readonly deviceFields = ['Manufacturer', 'ModelDevice', 'TypeDevice', 'SerialNoDevice', 'CodeNoDevice'];
  readonly rangeFields = ['From', 'To', 'Value', 'Division', 'Unit', 'UncertainyFloat'];
  readonly typeBFields = [
    'TindAdability', 'TrefResolution', 'TrefDrift',
    'TindZeroBefore', 'TindZeroAfter', 'TrefZeroBefore', 'TrefZeroAfter'
  ];
  readonly distributionTypes = [
    'Automatic', 'Normal', 'Rectangular', 'Trapezoidal', 'Triangular', 'StudentT'
  ];
  readonly decisionRules = [
    { value: 0, label: '0 - Stringent Acceptance / Stringent Rejection' },
    { value: 1, label: '1 - Simple Acceptance / Simple Rejection' },
    { value: 2, label: '2 - Stringent Acceptance / Relaxed Rejection' },
    { value: 3, label: '3 - Relaxed Acceptance / Stringent Rejection' },
    { value: 4, label: '4 - Stringent Acceptance / Simple Rejection' },
    { value: 5, label: '5 - Simple Acceptance / Stringent Rejection' }
  ];
  readonly distributionLabels: { [key: string]: string } = {
    Automatic: 'خودکار', Normal: 'نرمال', Rectangular: 'مستطیلی',
    Trapezoidal: 'ذوزنقه‌ای', Triangular: 'مثلثی', StudentT: 'تی استیودنت'
  };
  readonly help: Readonly<Record<string, ThermometerFieldHelp>> = thermometerFieldHelp;
  readonly fieldHelp: Readonly<Record<string, string>> = thermometerHelpText;
  readonly fieldLabels: Record<string, string> = Object.keys(thermometerFieldHelp).reduce((labels, key) => {
    labels[key] = thermometerFieldHelp[key].label;
    if (key.startsWith('TypeB.')) { labels[key.slice(6)] = thermometerFieldHelp[key].label; }
    return labels;
  }, {} as Record<string, string>);
  readonly environmentGroups = [
    { key: 'Pa_IND', label: 'فشار محیط', unit: 'hPa' },
    { key: 'Qa_IND', label: 'رطوبت نسبی', unit: '%RH' },
    { key: 'ta_IND', label: 'دمای محیط', unit: '°C' }
  ];

  readonly acceptanceFields = ['Tolerance', 'Limit', 'LowerLimit', 'UpperLimit', 'LimitValue', 'DecisionRule', 'Distribution'];
  private acceptanceOverrides = new WeakSet<AbstractControl>();
  private applyingDefaults = false;
  private readonly destroy$ = new Subject<void>();
  private routeId: number = null;
  private _entityId: number = null;

  @Input()
  set entityId(value: number) {
    this._entityId = this.positiveId(value);
    if (this.form) { this.load(); }
  }
  get entityId(): number { return this._entityId; }

  get certificateRegistrationMessage(): string | null {
    switch (this.certificateRegistrationStatus) {
      case 'registered':
        return 'ثبت گواهی انجام شده است';
      case 'notRegistered':
        return 'برای این دستگاه، ثبت گواهی انجام نشده است';
      default:
        return null;
    }
  }

  constructor(
    private fb: FormBuilder,
    private service: DataEntityThermometerFormService,
    private alertify: AlertifyService,
    private route: ActivatedRoute,
    private host: ElementRef
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.routeId = this.positiveId(params.get('id'));
      this.load();
    });
  }

  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }

  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent): void {
    const target = event.target as HTMLInputElement;
    if (target && target.type === 'number' && document.activeElement === target) { target.blur(); }
  }

  get Measurements(): FormArray { return this.form.get('Measurements') as FormArray; }
  get NModels(): FormArray { return this.form.get('NModels') as FormArray; }
  asGroup(control: AbstractControl): FormGroup { return control as FormGroup; }

  AddMeasurement(value: any = {}): void {
    const id = value.MeasurementId || this.nextMeasurementId();
    this.Measurements.push(this.createMeasurement({ ...value, MeasurementId: id }));
    if (!this.NModels.controls.some(x => String(x.get('MeasurementId').value) === String(id))) {
      this.AddNModel({ MeasurementId: id, Value: value.NominalTemperature });
    }
    this.form.updateValueAndValidity();
  }

  RemoveMeasurement(index: number): void {
    const id = String(this.Measurements.at(index).get('MeasurementId').value || '');
    this.Measurements.removeAt(index);
    const related = this.NModels.controls.findIndex(x => String(x.get('MeasurementId').value || '') === id);
    if (related >= 0) { this.NModels.removeAt(related); }
    this.form.updateValueAndValidity();
  }

  AddNModel(value: any = {}): void {
    const id = value.MeasurementId || this.firstMeasurementWithoutNModel() || this.nextMeasurementId();
    const row = this.createNModel({ ...value, MeasurementId: id });
    this.NModels.push(row);
    if (this.NModels.length > 1 && this.validAcceptance(this.NModels.at(0))) {
      this.applyingDefaults = true;
      row.patchValue(this.acceptanceValue(this.NModels.at(0)));
      this.applyingDefaults = false;
    }
    this.form.updateValueAndValidity();
  }

  RemoveNModel(index: number): void { this.NModels.removeAt(index); this.form.updateValueAndValidity(); }

  submitForm(): void {
    if (this.isSubmitting || this.isLoadingInitialData) { return; }
    this.submitAttempted = true;
    this.applyResolvedId();
    this.form.updateValueAndValidity();
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.focusFirstInvalid();
      return;
    }
    this.isSubmitting = true;
    this.service.save(this.buildPayload()).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.isSubmitting = false)
    ).subscribe(
      result => {
        this.applyResponse(result);
        this.certificateRegistrationStatus = 'registered';
        this.alertify.SuccessMessage('ثبت اطلاعات با موفقیت انجام شد.');
      },
      () => this.alertify.ErrorMessage('خطا در ذخیره اطلاعات.')
    );
  }

  download(path: string, type: 'certificate' | 'uncertaintyReport'): void {
    if (!path || this.isDownloading) { return; }
    this.isDownloading = true;
    this.service.downloadFile(path).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.isDownloading = false)
    ).subscribe(blob => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = path.split(/[\\/]/).pop() || (type === 'certificate' ? 'گواهی‌نامه.docx' : 'گزارش-عدم-قطعیت.docx');
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, () => this.alertify.ErrorMessage(type === 'certificate'
      ? 'خطا در دانلود گواهی‌نامه.' : 'خطا در دانلود گزارش عدم قطعیت.'));
  }

  toggleResult(index: number): void { this.expandedResultRows[index] = !this.expandedResultRows[index]; }

  displayNumber(value: number): string {
    if (value === null || value === undefined || !Number.isFinite(Number(value))) { return '—'; }
    const precision = Math.max(0, Math.min(10, Number(this.form.get('UncertainyFloat').value) || 0));
    return Number(value).toFixed(precision);
  }

  validationMessage(control: AbstractControl, select = false): string {
    if (control && (control.touched || control.dirty) && this.form?.hasError('distinctNominalTemperature') &&
        control === control.parent?.get('NominalTemperature')) {
      return 'برای محاسبه خط کالیبراسیون، حداقل دو دمای نامی متفاوت وارد کنید.';
    }
    if (control && (control.touched || control.dirty) && this.form && this.form.hasError('orderedRange') &&
        (control === this.form.get('From') || control === this.form.get('To'))) {
      return 'حد پایین محدوده نباید از حد بالای محدوده بیشتر باشد.';
    }
    if (control && (control.touched || control.dirty) && control.parent?.hasError('acceptanceLimits') &&
        (control === control.parent.get('LowerLimit') || control === control.parent.get('UpperLimit'))) {
      return 'حد پایین باید کوچک‌تر از حد بالا باشد.';
    }
    if (!control || !control.invalid || (!control.touched && !control.dirty)) { return ''; }
    if (control.hasError('required')) { return select ? 'لطفاً یک گزینه انتخاب کنید.' : 'این فیلد الزامی است.'; }
    if (control.hasError('min')) { return `مقدار باید حداقل ${control.getError('min').min} باشد.`; }
    if (control.hasError('max')) { return `مقدار نباید بیشتر از ${control.getError('max').max} باشد.`; }
    if (control.hasError('pattern')) { return 'فرمت مقدار واردشده صحیح نیست.'; }
    if (control.hasError('positive')) {
      if (control === this.form.get('Reference.CoverageFactor')) { return 'ضریب پوشش مرجع باید عددی بزرگ‌تر از صفر باشد.'; }
      if (control === control.parent?.get('Tolerance')) { return 'تلرانس باید عددی بزرگ‌تر از صفر باشد.'; }
      return 'عدد بزرگ‌تر از صفر وارد کنید.';
    }
    if (control.hasError('nonNegative')) {
      return control === control.parent?.get('LimitValue') ? 'حد شاخص قابلیت باید صفر یا بزرگ‌تر باشد.' : 'عدد منفی مجاز نیست.';
    }
    if (control.hasError('finiteNumber')) { return 'لطفاً یک مقدار عددی معتبر وارد کنید.'; }
    return 'مقدار معتبر وارد کنید.';
  }

  trackByIndex(index: number): number { return index; }

  private initializeForm(input?: any): void {
    this.submitAttempted = false;
    const source = input || {};
    const measurements = Array.isArray(source.Measurements) ? source.Measurements : [];
    const nModels = Array.isArray(source.NModels) ? source.NModels : [];
    this.form = this.fb.group({
      Description: [this.value(source.Description), requiredTextValidator],
      Expert: [this.value(source.Expert), requiredTextValidator],
      IssueDateMiladi: [this.value(source.IssueDateMiladi), requiredTextValidator],
      CalibrationDateMiladi: [this.value(source.CalibrationDateMiladi), requiredTextValidator],
      RecalibrationDateMiladi: [this.value(source.RecalibrationDateMiladi), requiredTextValidator],
      CustomerName: [this.value(source.CustomerName), requiredTextValidator],
      CertificateAddress: [this.value(source.CertificateAddress), requiredTextValidator],
      LabAdderss: [this.value(source.LabAdderss), requiredTextValidator],
      RequestId: [{ value: this.value(source.RequestId), disabled: true }],
      LetterId: [{ value: this.number(source.LetterId), disabled: true }],
      CalibrationDetailId: [{ value: this.number(source.CalibrationDetailId), disabled: true }],
      Manufacturer: [this.value(source.Manufacturer), requiredTextValidator],
      ModelDevice: [this.value(source.ModelDevice), requiredTextValidator],
      TypeDevice: [this.value(source.TypeDevice), requiredTextValidator],
      SerialNoDevice: [this.value(source.SerialNoDevice), requiredTextValidator],
      CodeNoDevice: [this.value(source.CodeNoDevice), requiredTextValidator],
      From: [this.number(source.From), finiteNumberValidator()],
      To: [this.number(source.To), finiteNumberValidator()],
      Value: [this.number(source.Value), finiteNumberValidator()],
      Division: [this.number(source.Division), nonNegativeFiniteNumberValidator()],
      Unit: [this.value(source.Unit, '°C'), requiredTextValidator],
      UncertainyFloat: [source.UncertainyFloat == null ? 2 : source.UncertainyFloat,
        [nonNegativeFiniteNumberValidator(), Validators.pattern(/^\d+$/)]],
      Pa_IND: this.createEnvironment(source.Pa_IND, 'hPa'),
      Qa_IND: this.createEnvironment(source.Qa_IND, '%RH'),
      ta_IND: this.createEnvironment(source.ta_IND, '°C'),
      Reference: this.createReference(source.Reference),
      TypeB: this.createTypeB(source.TypeB),
      Measurements: this.fb.array([]),
      NModels: this.fb.array([])
    }, { validators: [orderedRangeValidator(), thermometerArraysValidator()] });
    this.legacyMeasurements = measurements.filter(row => !Array.isArray(row.UutReadings) && (row.Tind != null || row.Tref != null));
    this.rebuildMeasurements(measurements);
    this.rebuildNModels(nModels);
    this.NModels.controls.slice(1).forEach(row => {
      if (Array.isArray(source.AcceptanceOverrides) ? source.AcceptanceOverrides.indexOf(row.get('MeasurementId').value) >= 0 : JSON.stringify(this.acceptanceValue(row)) !== JSON.stringify(this.acceptanceValue(this.NModels.at(0)))) { this.acceptanceOverrides.add(row); }
    });
    this.applyResolvedId();
    if (!this.Measurements.length) { this.AddMeasurement(); }
  }

  private rebuildMeasurements(measurements: any[]): void {
    this.Measurements.clear();
    (measurements || []).forEach(item => this.Measurements.push(this.createMeasurement(item)));
  }

  private rebuildNModels(nModels: any[]): void {
    this.NModels.clear();
    (nModels || []).forEach(item => this.NModels.push(this.createNModel(item)));
  }

  private createMeasurement(value: any = {}): FormGroup {
    return this.fb.group({
      MeasurementId: [this.value(value.MeasurementId).trim() || this.nextMeasurementId()],
      NominalTemperature: [this.number(value.NominalTemperature), finiteNumberValidator()],
      ReferenceBefore: [this.number(value.ReferenceBefore), finiteNumberValidator()],
      UutReadings: this.fb.array((Array.isArray(value.UutReadings) ? value.UutReadings : ['', '', ''])
        .map(x => this.fb.control(this.number(x), finiteNumberValidator())), control => control.value.length >= 3 ? null : { minimumReadings: true }),
      ReferenceAfter: [this.number(value.ReferenceAfter), finiteNumberValidator()],
      Instability: [this.number(value.Instability), nonNegativeFiniteNumberValidator()],
      Inhomogeneity: [this.number(value.Inhomogeneity), nonNegativeFiniteNumberValidator()]
      // Legacy single observations are never expanded into fabricated readings.
    });
  }

  private createNModel(value: any = {}): FormGroup {
    const row = this.fb.group({
      MeasurementId: [this.value(value.MeasurementId).trim() ||
        this.Measurements.at(this.NModels.length)?.get('MeasurementId').value || this.nextMeasurementId()],
      Value: [this.value(value.Value), requiredTextValidator],
      Tolerance: [this.number(value.Tolerance), positiveFiniteNumberValidator()],
      Limit: [this.number(value.Limit), finiteNumberValidator()],
      LowerLimit: [this.number(value.LowerLimit), finiteNumberValidator()],
      UpperLimit: [this.number(value.UpperLimit), finiteNumberValidator()],
      LimitValue: [this.number(value.LimitValue), nonNegativeFiniteNumberValidator()],
      DecisionRule: [value.DecisionRule == null ? '' : value.DecisionRule, [requiredSelectionValidator, Validators.min(0), Validators.max(5), Validators.pattern(/^[0-5]$/)]],
      Distribution: this.createDistribution(value.Distribution, 'Normal')
    }, { validators: acceptanceLimitsValidator() });
    let previous = JSON.stringify(this.acceptanceValue(row));
    row.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const current = JSON.stringify(this.acceptanceValue(row));
      if (current === previous) { return; }
      previous = current;
      if (this.applyingDefaults) { return; }
      const index = this.NModels.controls.indexOf(row);
      if (index === 0 && this.validAcceptance(row)) {
        this.NModels.controls.slice(1).filter(other => !this.acceptanceOverrides.has(other)).forEach(other => {
          this.applyingDefaults = true;
          other.patchValue(this.acceptanceValue(row));
          this.applyingDefaults = false;
        });
      } else if (index > 0) { this.acceptanceOverrides.add(row); }
    });
    return row;
  }

  private acceptanceValue(row: AbstractControl): any {
    const value: any = {};
    this.acceptanceFields.forEach(key => value[key] = row.get(key).value);
    return value;
  }
  private validAcceptance(row: AbstractControl): boolean {
    return !!row && this.acceptanceFields.every(key => row.get(key).valid) && !row.hasError('acceptanceLimits');
  }
  isAcceptanceOverride(row: AbstractControl): boolean { return this.acceptanceOverrides.has(row); }
  useAcceptanceDefault(index: number): void {
    if (index < 1 || !this.validAcceptance(this.NModels.at(0))) { return; }
    const row = this.NModels.at(index);
    this.applyingDefaults = true;
    row.patchValue(this.acceptanceValue(this.NModels.at(0)));
    this.applyingDefaults = false;
    this.acceptanceOverrides.delete(row);
  }
  readings(row: AbstractControl): FormArray { return row.get('UutReadings') as FormArray; }
  addReading(index: number): void { this.readings(this.Measurements.at(index)).push(this.fb.control('', finiteNumberValidator())); }
  removeReading(index: number, reading: number): void { this.readings(this.Measurements.at(index)).removeAt(reading); }
  preview(row: AbstractControl): any {
    if (row.invalid || this.readings(row).length < 3) { return {}; }
    const value = row.value, xs = value.UutReadings.map(Number), n = xs.length;
    const meanReference = Number(value.ReferenceBefore) / 2 + Number(value.ReferenceAfter) / 2;
    const meanUut = xs.reduce((a, b) => a + b, 0) / n;
    const sd = Math.sqrt(xs.reduce((a, b) => a + Math.pow(b - meanUut, 2), 0) / (n - 1));
    return { meanReference, meanUut, correction: meanReference - meanUut, sd, ua: sd / Math.sqrt(n) };
  }

  private createEnvironment(value: any = {}, unit: string): FormGroup {
    const source = value || {};
    return this.fb.group({
      Value: [this.number(source.Value), finiteNumberValidator()],
      Unit: [this.value(source.Unit, unit), requiredTextValidator],
      HalfWidthOfLimit: [this.number(source.HalfWidthOfLimit), nonNegativeFiniteNumberValidator()],
      Distribution: this.createDistribution(source.Distribution, 'Rectangular'),
      standardUncertainty: [source.standardUncertainty == null ? source.StandardUncertainty : source.standardUncertainty],
      sensitivityCoeff: [source.sensitivityCoeff == null ? source.SensitivityCoeff : source.sensitivityCoeff],
      uncertaintyContribution: [source.uncertaintyContribution == null ? source.UncertaintyContribution : source.uncertaintyContribution],
      relativeContribution: [source.relativeContribution == null ? source.RelativeContribution : source.relativeContribution]
    });
  }

  private createDistribution(value: any = {}, type: string): FormGroup {
    const source = value || {};
    return this.fb.group({
      '$type': [this.value(source.$type, type), [requiredTextValidator,
        Validators.pattern(/^(Automatic|Normal|Rectangular|Trapezoidal|Triangular|StudentT)$/)]],
      CoverageFactor: [source.CoverageFactor == null ? (type === 'Normal' ? 2 : 1.65) : source.CoverageFactor, positiveFiniteNumberValidator()],
      ShapeFactor: [source.ShapeFactor == null ? 1 : source.ShapeFactor, positiveFiniteNumberValidator()]
    });
  }

  private createReference(value: any = {}): FormGroup {
    const source = value || {};
    return this.fb.group({
      TrefCalValue: [this.number(source.TrefCalValue), finiteNumberValidator()],
      ExpandedUncertainty: [this.number(source.ExpandedUncertainty), nonNegativeFiniteNumberValidator()],
      CoverageFactor: [source.CoverageFactor == null ? 2 : source.CoverageFactor, positiveFiniteNumberValidator()],
      Distribution: [this.value(source.Distribution, 'Normal'), [requiredTextValidator,
        Validators.pattern(/^(Automatic|Normal|Rectangular|Trapezoidal|Triangular|StudentT)$/)]]
    });
  }

  private createTypeB(value: any = {}): FormGroup {
    const controls: { [key: string]: AbstractControl } = {};
    this.typeBFields.forEach(key => controls[key] = this.fb.control(this.number(value && value[key]), finiteNumberValidator()));
    return this.fb.group(controls);
  }

  private load(): void {
    const id = this._entityId || this.routeId;
    if (!id || this.isLoadingInitialData) { return; }
    this.isLoadingInitialData = true;
    this.service.get(id).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.isLoadingInitialData = false)
    ).subscribe(result => this.applyResponse(result), error => {
      if (error.status === 404) {
        this.form.get('CalibrationDetailId').setValue(id);
        this.certificateRegistrationStatus = 'notRegistered';
      } else {
        this.certificateRegistrationStatus = 'unknown';
        this.alertify.ErrorMessage('خطا در دریافت اطلاعات گواهی‌نامه.');
      }
    });
  }

  private applyResponse(envelope: ThermometerApiEnvelope): void {
    const raw: any = envelope && envelope.data != null ? envelope.data : envelope || {};
    const certificate = this.pick(raw, 'certificate', 'Certificate', 'result', 'Result') || {};
    const directInput = this.pick(raw, 'input', 'Input', 'thermometerInput', 'ThermometerInput',
      'thermometerModel', 'ThermometerModel') || this.pick(certificate, 'input', 'Input') ||
      (this.pick(raw, 'CalibrationDetailId', 'calibrationDetailId') !== undefined ? raw : null);
    const input = directInput
      ? this.normalizeThermometerInput(directInput)
      : (this.form ? this.form.getRawValue() : {});
    const resolvedId = this._entityId || this.routeId;
    if (!isFiniteNumericValue(input.CalibrationDetailId) && resolvedId) { input.CalibrationDetailId = resolvedId; }
    if (directInput) { this.initializeForm(input); }
    const resultSource = this.pick(raw, 'results', 'Results') !== undefined ? raw : certificate;
    const rawResults = this.pick(resultSource, 'results', 'Results');
    const results = Array.isArray(rawResults) ? rawResults.map(item => this.normalizeResult(item)) : [];
    this.response = {
      input,
      lineA: this.pick(resultSource, 'lineA', 'LineA'),
      lineB: this.pick(resultSource, 'lineB', 'LineB'),
      linearity: this.pick(resultSource, 'linearity', 'Linearity'),
      results: results as ThermometerResult[]
    };
    this.expandedResultRows = results.map(() => false);
    const fileSources = [raw, certificate, this.pick(certificate, 'input', 'Input'), directInput, input];
    this.certificateFilePath = this.firstFileValue(fileSources,
      'certificatePath', 'CertificatePath', 'certificatefile', 'certificateFile', 'Certificatefile', 'CertificateFile');
    this.uncertaintyReportFilePath = this.firstFileValue(fileSources,
      'gufMcmReportPath', 'GufMcmReportPath', 'uncertaintyReportPath', 'UncertaintyReportPath',
      'uncertaintyReportFile', 'UncertaintyReportFile');
    this.certificateRegistrationStatus = this.certificateFilePath
      ? 'registered'
      : 'notRegistered';
  }

  buildPayload(): ThermometerCertificateRequest {
    if (this.form.invalid) { throw new Error('فرم نامعتبر است.'); }
    if (this.Measurements.controls.some(row => this.readings(row).length < 3)) { throw new Error('حداقل سه قرائت الزامی است.'); }
    const raw = this.form.getRawValue();
    const numeric = (value: any): number => {
      if (!isFiniteNumericValue(value)) { throw new Error('مقدار عددی نامعتبر است.'); }
      return Number(typeof value === 'string' ? value.trim() : value);
    };
    const numbers = (source: any, fields: string[]): any => {
      const result: any = {};
      fields.forEach(key => result[key] = numeric(source[key]));
      return result;
    };
    const distribution = (source: any) => ({
      $type: String(source.$type), ...numbers(source, ['CoverageFactor', 'ShapeFactor'])
    });
    const environment = (source: any) => ({
      ...numbers(source, ['Value', 'HalfWidthOfLimit']), Unit: String(source.Unit),
      Distribution: distribution(source.Distribution)
    });
    const payload: any = {};
    [...this.generalFields, ...this.deviceFields, 'Unit'].forEach(key => payload[key] = String(raw[key]).trim());
    payload.RequestId = this.value(raw.RequestId);
    Object.assign(payload, numbers(raw, ['From', 'To', 'Value', 'Division', 'UncertainyFloat']));
    // System IDs are supplied by the route/GET, never required user input or fabricated zeroes.
    const calibrationId = isFiniteNumericValue(raw.CalibrationDetailId)
      ? raw.CalibrationDetailId : this._entityId || this.routeId;
    if (isFiniteNumericValue(calibrationId)) { payload.CalibrationDetailId = numeric(calibrationId); }
    if (isFiniteNumericValue(raw.LetterId)) { payload.LetterId = numeric(raw.LetterId); }
    payload.Pa_IND = environment(raw.Pa_IND);
    payload.Qa_IND = environment(raw.Qa_IND);
    payload.ta_IND = environment(raw.ta_IND);
    payload.Reference = {
      ...numbers(raw.Reference, ['TrefCalValue', 'ExpandedUncertainty', 'CoverageFactor']),
      Distribution: String(raw.Reference.Distribution)
    };
    payload.ContractVersion = 2;
    payload.AcceptanceOverrides = this.NModels.controls.filter(row => this.acceptanceOverrides.has(row))
      .map(row => String(row.get('MeasurementId').value));
    payload.TypeB = numbers(raw.TypeB, this.typeBFields);
    payload.Measurements = raw.Measurements.map(row => ({
      MeasurementId: String(row.MeasurementId).trim(),
      ...numbers(row, ['NominalTemperature', 'ReferenceBefore', 'ReferenceAfter', 'Instability', 'Inhomogeneity']),
      UutReadings: row.UutReadings.map(numeric)
    }));
    payload.NModels = raw.NModels.map(row => ({
      MeasurementId: String(row.MeasurementId).trim(), Value: String(row.Value).trim(),
      ...numbers(row, ['Tolerance', 'Limit', 'LowerLimit', 'UpperLimit', 'LimitValue', 'DecisionRule']),
      Distribution: distribution(row.Distribution)
    }));
    return payload as ThermometerCertificateRequest;
  }

  private normalizeThermometerInput(source: any): any {
    const result: any = {};
    const scalarFields = [
      'AcceptanceOverrides', 'Description', 'Expert', 'IssueDateMiladi', 'CalibrationDateMiladi', 'RecalibrationDateMiladi',
      'Manufacturer', 'ModelDevice', 'TypeDevice', 'SerialNoDevice', 'CodeNoDevice', 'LabAdderss',
      'CustomerName', 'CertificateAddress', 'RequestId', 'CalibrationDetailId', 'LetterId', 'From', 'To',
      'Value', 'Division', 'Unit', 'UncertainyFloat', 'certificatefile', 'uncertaintyReportFile'
    ];
    scalarFields.forEach(key => {
      const value = this.pick(source, key, key.charAt(0).toLowerCase() + key.slice(1));
      if (value !== undefined) { result[key] = value; }
    });
    result.Pa_IND = this.normalizeEnvironment(this.pick(source, 'Pa_IND', 'pa_IND'));
    result.Qa_IND = this.normalizeEnvironment(this.pick(source, 'Qa_IND', 'qa_IND'));
    result.ta_IND = this.normalizeEnvironment(this.pick(source, 'ta_IND', 'Ta_IND'));
    result.Reference = this.normalizeObject(this.pick(source, 'Reference', 'reference'),
      ['TrefCalValue', 'ExpandedUncertainty', 'CoverageFactor', 'Distribution']);
    result.TypeB = this.normalizeObject(this.pick(source, 'TypeB', 'typeB'), this.typeBFields);
    const measurements = this.pick(source, 'Measurements', 'measurements');
    result.Measurements = Array.isArray(measurements)
      ? measurements.map(item => this.normalizeObject(item, ['MeasurementId', 'NominalTemperature', 'Tind', 'Tref', 'ReferenceBefore', 'ReferenceAfter', 'UutReadings', 'Instability', 'Inhomogeneity'])) : [];
    const nModels = this.pick(source, 'NModels', 'nModels');
    result.NModels = Array.isArray(nModels) ? nModels.map(item => {
      const model = this.normalizeObject(item, [
        'MeasurementId', 'Value', 'Tolerance', 'Limit', 'LowerLimit', 'UpperLimit', 'LimitValue', 'DecisionRule'
      ]);
      model.Distribution = this.normalizeDistribution(this.pick(item, 'Distribution', 'distribution'));
      return model;
    }) : [];
    return result;
  }

  private normalizeEnvironment(source: any): any {
    const result = this.normalizeObject(source, [
      'Value', 'Unit', 'HalfWidthOfLimit', 'standardUncertainty', 'sensitivityCoeff',
      'uncertaintyContribution', 'relativeContribution'
    ]);
    result.Distribution = this.normalizeDistribution(this.pick(source, 'Distribution', 'distribution'));
    return result;
  }

  private normalizeDistribution(source: any): any {
    return {
      $type: this.pick(source, '$type'),
      CoverageFactor: this.pick(source, 'CoverageFactor', 'coverageFactor'),
      ShapeFactor: this.pick(source, 'ShapeFactor', 'shapeFactor')
    };
  }

  private normalizeObject(source: any, fields: string[]): any {
    const result: any = {};
    fields.forEach(key => {
      const value = this.pick(source, key, key.charAt(0).toLowerCase() + key.slice(1));
      if (value !== undefined) { result[key] = value; }
    });
    return result;
  }

  private normalizeResult(source: any): ThermometerResult {
    const result: any = {};
    [
      'measurementId', 'nominalTemperature', 'tind', 'tref', 'hysteresisCorrection', 'correction',
      'correctionFromLineEquation', 'errorValue', 'combinedUncertainty', 'expandedUncertainty',
      'capability', 'pInside', 'pOutside', 'compliance', 'decision', 'readingCount', 'sampleStandardDeviation', 'typeAStandardUncertainty', 'typeADegreesOfFreedom'
    ].forEach(key => result[key] = this.pick(source, key, key.charAt(0).toUpperCase() + key.slice(1)));
    const budget = this.pick(source, 'uncertaintyBudget', 'UncertaintyBudget');
    result.uncertaintyBudget = Array.isArray(budget) ? budget.map(item => ({
      value: this.pick(item, 'value', 'Value'),
      unit: this.pick(item, 'unit', 'Unit'),
      degreesOfFreedom: this.pick(item, 'degreesOfFreedom', 'DegreesOfFreedom'),
      relativeContribution: this.pick(item, 'relativeContribution', 'RelativeContribution'),
      name: this.pick(item, 'name', 'Name'),
      distribution: this.pick(item, 'distribution', 'Distribution'),
      standardUncertainty: this.pick(item, 'standardUncertainty', 'StandardUncertainty'),
      sensitivityCoefficient: this.pick(item, 'sensitivityCoefficient', 'SensitivityCoefficient'),
      contribution: this.pick(item, 'contribution', 'Contribution')
    })) : [];
    return result as ThermometerResult;
  }

  private pick(source: any, ...keys: string[]): any {
    if (!source || typeof source !== 'object') { return undefined; }
    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(source, key)) { return source[key]; }
    }
    return undefined;
  }

  private applyResolvedId(): void {
    const id = this._entityId || this.routeId;
    if (id && this.form && !isFiniteNumericValue(this.form.get('CalibrationDetailId').value)) {
      this.form.get('CalibrationDetailId').setValue(id, { emitEvent: false });
    }
  }

  private nextMeasurementId(): string {
    const used = this.Measurements ? this.Measurements.controls.map(x => String(x.get('MeasurementId').value)) : [];
    let index = 1;
    while (used.indexOf(`T${index}`) >= 0) { index++; }
    return `T${index}`;
  }

  private firstMeasurementWithoutNModel(): string {
    const used = this.NModels.controls.map(x => String(x.get('MeasurementId').value));
    const measurement = this.Measurements.controls.find(x => used.indexOf(String(x.get('MeasurementId').value)) < 0);
    return measurement ? String(measurement.get('MeasurementId').value) : null;
  }

  private fileValue(raw: any, input: any, ...keys: string[]): string {
    for (const key of keys) {
      if (raw && typeof raw[key] === 'string' && raw[key].trim()) { return raw[key]; }
      if (input && typeof input[key] === 'string' && input[key].trim()) { return input[key]; }
    }
    return null;
  }
  private firstFileValue(sources: any[], ...keys: string[]): string {
    for (const source of sources) {
      for (const key of keys) {
        const value = this.pick(source, key);
        if (typeof value === 'string' && value.trim()) { return value.trim(); }
      }
    }
    return null;
  }

  private value(value: any, fallback = ''): string { return value == null ? fallback : String(value); }
  private number(value: any): any { return value == null ? '' : value; }
  private positiveId(value: any): number { const id = Number(value); return Number.isFinite(id) && id > 0 ? id : null; }
  private handleError(error: HttpErrorResponse): void {
    const details: any = error && error.error;
    this.alertify.ErrorMessage(details && (details.detail || details.title || details.message) || 'خطا در ارتباط با سرویس Thermometer');
  }
  private focusFirstInvalid(): void {
    setTimeout(() => {
      const input = this.host.nativeElement.querySelector(
        'input.ng-invalid:not([readonly]):not(:disabled), select.ng-invalid:not(:disabled), ' +
        'textarea.ng-invalid:not([readonly]):not(:disabled), input.range-error') ||
        this.host.nativeElement.querySelector('.validation-summary');
      if (input) {
        if (input.scrollIntoView) { input.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
        if (input.focus) { input.focus({ preventScroll: true }); }
      }
    });
  }
}
